import { useState, useEffect, useRef } from 'react';
import { api } from '../api/client';

const NODES = [
  { role: 'gateway_moe',     name: 'Gateway MoE',       icon: '⬡', desc: 'Router & Classifier (qwen3-30b-a3b / gpt-oss-20b)' },
  { role: 'architect_llama', name: 'Architect Planner', icon: '🏗', desc: 'Dựng Spec & Blueprint (deepseek-v4-pro / gpt-oss-120b)' },
  { role: 'artisan_mistral', name: 'Artisan Coder',     icon: '⚡', desc: 'Viết code chính & ReAct (gpt-oss-120b / gpt-oss-20b / qwen3-a3b)' },
  { role: 'image_to_text',   name: 'Vision & Workflow', icon: '👁️', desc: 'Phân tích ảnh & Workflow diagram (gemini-2.5-flash)' },
  { role: 'reasoner_r1',     name: 'Reasoner & QA',     icon: '🧠', desc: 'Thẩm định QA & Reasoning (gpt-oss-120b / deepseek-v4-pro)' },
  { role: 'gpt_120b_reviewer', name: 'Reviewer Vòng 3', icon: '🔍', desc: 'Tư duy ngược fix lỗi (gpt-oss-120b)' },
  { role: 'claude_expert',   name: 'Master Resolver',   icon: '🤖', desc: 'Cứu nguy Vòng 4, 5 (deepseek-v4-pro-0813)' },
  { role: 'commander_node',  name: 'Commander Node',    icon: '🛡️', desc: 'Phân tích & Ra lệnh fix (gpt-oss-120b / gpt-oss-20b)' },
  { role: 'scout_glm',       name: 'Scout Web & Docs',  icon: '🔭', desc: 'Thu thập tài liệu 1M/128k (gemini-2.5-flash-lite / gpt-oss-20b / qwen3-a3b)' },
  { role: 'embedder',        name: 'Embedder BAAI',     icon: '🧩', desc: 'Vector embedding (bge-m3)' },
  { role: 'text_to_image',   name: 'Text to Image',     icon: '🎨', desc: 'Tạo ảnh từ text (@cf/black-forest-labs/flux-1-schnell)' }
];

const PROVIDERS = ['cloudflare', 'newapi', 'openai', 'anthropic', 'gemini', 'groq', 'together', 'deepseek', 'openrouter'];

export function NodesPage() {
  const [selected, setSelected] = useState(NODES[0]!.role);
  const [promptData, setPromptData] = useState<any>(null);
  const [chainData, setChainData] = useState<any>(null);
  const [registry, setRegistry] = useState<any>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [chain, setChain] = useState<any[]>([]);

  const node = NODES.find(n => n.role === selected)!;

  useEffect(() => {
    setPromptData(null);
    setChainData(null);
    setMsg('');

    Promise.all([
      api.getPrompt(selected),
      api.getProviderConfig(),
      api.getModelRegistry(),
    ]).then(([p, c, r]) => {
      setPromptData(p);
      setEditedPrompt(p.prompt);
      setChainData(c);
      setRegistry(r.registry);
      const nodeChain = c.nodes?.[selected]?.chain ?? r.default_chains?.[selected] ?? [
        { provider: 'cloudflare', model: r.registry?.[selected]?.equivalents?.cloudflare ?? '', enabled: true, priority: 0 }
      ];
      setChain(nodeChain);
    }).catch(() => {});
  }, [selected]);

  const savePrompt = async () => {
    setSaving(true);
    try {
      await api.updatePrompt(selected, editedPrompt);
      setMsg('✓ Prompt saved');
    } catch (e) {
      setMsg('✗ ' + String(e));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const resetPrompt = async () => {
    if (!confirm('Reset to default prompt?')) return;
    await api.resetPrompt(selected);
    const p = await api.getPrompt(selected);
    setEditedPrompt(p.prompt);
    setMsg('✓ Reset to default');
    setTimeout(() => setMsg(''), 3000);
  };

  const saveChain = async () => {
    setSaving(true);
    try {
      await api.updateNodeChain(selected, chain);
      setMsg('✓ Provider chain saved');
    } catch (e) {
      setMsg('✗ ' + String(e));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const saveApiKeys = async () => {
    setSaving(true);
    try {
      await api.updateGlobalApiKeys(chainData.api_keys || {});
      setMsg('✓ Global API keys saved');
    } catch (e) {
      setMsg('✗ ' + String(e));
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const addProvider = () => {
    setChain(c => [...c, { provider: 'openai', model: '', enabled: true, priority: c.length }]);
  };

  const removeProvider = (i: number) => setChain(c => c.filter((_, idx) => idx !== i));

  const updateChainItem = (i: number, key: string, value: any) => {
    setChain(c => c.map((item, idx) => {
      if (idx !== i) return item;
      const updated = { ...item, [key]: value };
      // Auto-fill model from registry
      if (key === 'provider' && registry?.[selected]) {
        updated.model = registry[selected].equivalents?.[value] ?? '';
      }
      return updated;
    }));
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Nodes</h1>
          <p className="page-subtitle">Configure providers, models, and system prompts per node</p>
        </div>
        {msg && <span style={{ fontSize: '13px', color: msg.startsWith('✓') ? 'var(--accent-green)' : 'var(--accent-red)' }}>{msg}</span>}
      </div>

      {/* Node selector */}
      <div className="node-grid" style={{ marginBottom: '28px' }}>
        {NODES.map(n => (
          <div
            key={n.role}
            className={`node-card ${selected === n.role ? 'selected' : ''}`}
            onClick={() => setSelected(n.role)}
          >
            <div className="node-card__header">
              <div className="node-card__icon">{n.icon}</div>
              <div>
                <div className="node-card__name">{n.name}</div>
                <div className="node-card__model">{n.role}</div>
              </div>
            </div>
            <p className="node-card__desc">{registry?.[n.role]?.description || n.desc}</p>
          </div>
        ))}
      </div>

      {!promptData ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

          {/* Provider Chain */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div className="card__title" style={{ margin: 0 }}>Provider Chain</div>
              <button className="btn btn--secondary btn--sm" onClick={addProvider}>+ Add</button>
            </div>

            {chain.map((item, i) => (
              <div key={i} className="chain-item">
                <div className="chain-item__priority">{i}</div>
                <div style={{ flex: 1 }}>
                  <select
                    className="select"
                    style={{ marginBottom: '6px' }}
                    value={item.provider}
                    onChange={e => updateChainItem(i, 'provider', e.target.value)}
                  >
                    {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input
                    className="input"
                    placeholder="model name..."
                    value={item.model}
                    onChange={e => updateChainItem(i, 'model', e.target.value)}
                    style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}
                  />
                  {item.provider === 'newapi' && (
                    <input
                      className="input"
                      placeholder="base_url (e.g. https://api.example.com/v1)"
                      value={item.base_url ?? ''}
                      onChange={e => updateChainItem(i, 'base_url', e.target.value)}
                      style={{ marginTop: '6px', fontSize: '12px' }}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={item.enabled} onChange={e => updateChainItem(i, 'enabled', e.target.checked)} />
                    on
                  </label>
                  <button className="btn btn--danger btn--sm" onClick={() => removeProvider(i)} title="Remove">✕</button>
                </div>
              </div>
            ))}

            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                Priority 0 = primary. Auto-fallback to next if primary fails.
              </div>
              <button className="btn btn--primary w-full" onClick={saveChain} disabled={saving} style={{ justifyContent: 'center' }}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save Provider Chain'}
              </button>
            </div>

            {/* Model equivalents reference */}
            {registry?.[selected] && (
              <div style={{ marginTop: '16px', padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>Suggested models for {node.name}</div>
                {Object.entries(registry[selected].equivalents as Record<string, string>).map(([prov, model]) => (
                  <div key={prov} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{prov}</span>
                    <code style={{ fontFamily: 'var(--font-mono)', color: '#a78bfa', fontSize: '10px' }}>{model}</code>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* System Prompt */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="card__title" style={{ margin: 0 }}>System Prompt</span>
                {promptData.is_custom && <span className="badge badge--amber">custom</span>}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn--secondary btn--sm" onClick={resetPrompt}>Reset</button>
                <button className="btn btn--primary btn--sm" onClick={savePrompt} disabled={saving}>
                  {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Save'}
                </button>
              </div>
            </div>
            <textarea
              className="textarea"
              style={{ minHeight: '420px', fontSize: '12px' }}
              value={editedPrompt}
              onChange={e => setEditedPrompt(e.target.value)}
              spellCheck={false}
            />
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px' }}>
              {editedPrompt.length} chars · ~{Math.ceil(editedPrompt.length / 4)} tokens
            </div>
          </div>
        </div>
      )}

      {/* Global API Keys Section */}
      {chainData && (
        <div className="card" style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div className="card__title" style={{ margin: 0 }}>Global API Keys</div>
            <button className="btn btn--primary btn--sm" onClick={saveApiKeys} disabled={saving}>
              {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Save Keys'}
            </button>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Enter API tokens for the providers. These keys are stored securely in KV and apply to all nodes. 
            Keys set here will override the environment variables.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {['cloudflare_account_id', 'cloudflare_api_token', ...PROVIDERS.filter(p => p !== 'cloudflare')].map(prov => (
              <div key={prov} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  {prov.replace('_', ' ').toUpperCase()}
                </label>
                <input
                  type="password"
                  className="input"
                  placeholder={`Enter ${prov} token...`}
                  value={chainData.api_keys?.[prov] ?? ''}
                  onChange={e => {
                    setChainData((prev: any) => ({
                      ...prev,
                      api_keys: { ...(prev.api_keys || {}), [prov]: e.target.value }
                    }));
                  }}
                  style={{ fontSize: '12px', fontFamily: 'var(--font-mono)' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
